import apiService, { getToken } from './api';
import { ApiError, type ApiResponse } from '../types/api';
import type {
  CropType, StakeholderType, Cultivar, CultivarProtection,
  CultivarProgram, CultivarProgramType, ProgramFormData, LicenseTier,
  RegionalLicense, ProductionDeclaration, EntitlementCertificate,
  NurseryCultivar, ComplianceDocument, ManagementGuide,
  ComplianceAlert, AuditLogEntry, ComplianceDashboard,
  CertificateVerification, Pagination,
  AttributeDefinition, AttributeValue, AttributeDataType,
  EvaluatorGrant, CultivarMedia, VisibilityConfig,
  CultivarEvaluation, EvaluationInput, EvaluationFollowup,
  EvaluationMedia, SeasonPosition, HarvestReferences, Hemisphere,
  GrowingRegion, CultivarRegionMaturity, RegionOffset, SeasonIndex,
  MaturityChart, MaturityChartFilters,
  CultivarInfo, CultivarInfoProposal, CultivarInfoSave,
  QuickEvaluation, QuickEvaluationInput, QuickEvaluationGroup, QuickMatchSuggestion,
  QuickMatchResult, QuickEvaluationPhoto, CultivarSalesNote,
} from '../types/cultivar';
import { maturityChartQuery } from '../utils/maturityChart';
import { openDocument } from '../utils/openDocument';
import type { DeclaredMaterialSource, MaterialSource, MaterialSourceInput } from '../types/propagation';
import type { LicensePreview } from '../types/contracts';

/** The details of an agreement to send; see issueLicenseFromTemplate. */
export interface IssueLicenseInput {
  /** The contract. Omitted for a master, which takes its group's. */
  family_id?: number | null;
  cultivar_id: number | null;
  sublicence_group_id?: number | null;
  /** The program a program master covers; only that tier names one. */
  program_id?: number | null;
  licensee_org_id: number;
  license_tier: LicenseTier;
  /** The rootstock the licensed trees are worked on — the second component
   *  of a composite tree, possibly protected by a different rights holder. */
  rootstock_cultivar_id?: number | null;
  supplying_nursery_org_id?: number | null;
  region?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  max_hectares?: number | null;
  max_trees?: number | null;
  tolerance_pct?: number;
  /** Only the fields the contract text has that the server cannot fill itself. */
  merge_values?: Record<string, string>;
  notify_user_id?: number | null;
}

/** An organisation holding an active propagation licence for a cultivar. */
export interface AuthorisedNursery {
  id: number;
  name: string;
  /**
   * Which right this nursery holds. A direct propagation licence carries a
   * number, an end date and a tree cap; a master sublicence plus a tick for
   * this cultivar carries none of those — its volume is set per order on the
   * schedule. The two read differently on screen, so they are not flattened.
   */
  source: 'direct_licence' | 'master_sublicence';
  /** The direct propagation licence. Null when the right comes from a master. */
  license_id: number | null;
  /** The master, when `source` is 'master_sublicence'. */
  master_license_id?: number | null;
  group_code?: string | null;
  /** null means the agreement behind this right has no end date. */
  end_date: string | null;
  region: string | null;
  country: string | null;
  max_trees: number | null;
}

/**
 * A mapped cultivar area claiming a nursery as the supplier of its trees.
 * The claimed figures are the orchard's: a tree count derived from a drawn
 * polygon, and whatever planting year somebody remembered.
 */
export interface SupplyClaim {
  cultivar_area_id: number;
  area_name: string;
  claimed_tree_count: number | null;
  planting_date: string | null;
  claimed_planting_year: number | null;
  area_hectares: number | null;
  orchard_org_id: number;
  orchard_name: string;
  cultivar_id: number | null;
  cultivar_name: string | null;
  cultivar_trade_name: string | null;
  cultivar_protection_status: string | null;
  block_name: string | null;
  farm_name: string | null;
  license_id: number | null;
  agreement_basis: 'factree' | 'pre_existing' | null;
  /** null until this nursery has answered. */
  verification_id: number | null;
  verification_status: 'confirmed' | 'corrected' | 'disputed' | null;
  verified_tree_count: number | null;
  verified_planting_year: number | null;
  verification_notes: string | null;
  verified_at: string | null;
}

/**
 * Permission to propagate a cultivar, and the terms it was granted on.
 *
 * The row IS the eligibility — there is no separate flag. `per_tree_fee` null
 * means the propagator pays whatever the programme charges; 0 means the rights
 * holder decided to charge nothing, which is a different statement.
 *
 * `conditions` are the LICENSOR's, imposed as a condition of producing. They
 * are not the nursery's terms of sale to an orchard — that is a private
 * agreement between those two parties.
 */
// =============================================================================
// SUBLICENSING
//
// A nursery signs ONE master agreement against a group. That admits it to the
// group and to nothing else — cultivars are placed into a group one at a time
// by their rights holder. Nothing here propagates in the other direction.
// =============================================================================

/**
 * A licensor-defined category a master nursery sublicence is written against.
 * The group scopes the AGREEMENT. Which cultivars a nursery may grow is ticked
 * per nursery on each cultivar's own screen — see [[CultivarSublicensing]].
 */
export interface SublicenceGroup {
  id: number;
  licensor_org_id: number;
  /** The licensor's own shorthand: 'A', 'PREMIUM'. */
  code: string;
  name: string;
  description: string | null;
  status: 'active' | 'closed';
  /** How many nurseries hold a live master against it. */
  active_nursery_count?: number;
  /**
   * The contract every nursery in the group signs as its master, and the one
   * issued under it per website order. Fixed here so issuing offers no choice
   * of document. Null means none set: issuing is refused until it is.
   */
  contract_family_id?: number | null;
  contract_family_name?: string | null;
  contract_current_version?: number | null;
  schedule_contract_family_id?: number | null;
  schedule_contract_family_name?: string | null;
  schedule_contract_current_version?: number | null;
  created_at: string;
  updated_at: string;
}

/** A nursery admitted to a group, and the master that admitted it. */
export interface GroupNursery {
  license_id: number;
  status: string;
  start_date: string | null;
  end_date: string | null;
  region: string | null;
  country: string | null;
  signed_at: string | null;
  nursery_org_id: number;
  nursery_name: string;
  is_house_nursery: boolean;
}

/** A nursery holding a master, and whether this cultivar is granted to it. */
export interface SublicensableNursery {
  nursery_org_id: number;
  nursery_name: string;
  master_license_id: number;
  /** The master's own status — a lapsed one still lists, and says so. */
  master_status: string;
  master_end_date: string | null;
  sublicence_group_id: number | null;
  group_code: string | null;
  /** Whether this cultivar has been ticked for this nursery. */
  granted: boolean;
  /**
   * Whether the tick may be changed. False until the nursery has SIGNED its
   * master — a sent-but-unsigned agreement is not authority to put a company
   * in the catalogue. Already-granted rows stay selectable so a lapsed master
   * does not trap a tick the licensor may want to clear.
   */
  selectable: boolean;
}

/** Whether a cultivar may be sublicensed, and by which nurseries. */
export interface CultivarSublicensing {
  id: number;
  is_sublicensable: boolean;
  show_on_website: boolean;
  /** Every nursery holding a master from us, ticked or not. */
  nurseries: SublicensableNursery[];
}

export interface CultivarSupplier {
  kind: 'house_nursery' | 'sublicensed_nursery';
  org_id: number;
  name: string;
  master_license_id: number | null;
  sublicence_group_id: number | null;
  group_code: string | null;
}

/** Exactly what the public website will offer for a cultivar. */
export interface CultivarSuppliers {
  cultivar_id: number;
  cultivar_name: string;
  is_sublicensable: boolean;
  suppliers: CultivarSupplier[];
  /**
   * Always available, and never a supplier. An orchard holds no master
   * sublicence and does not acquire one by picking this — it produces an
   * enquiry the rights holder sets up by hand.
   */
  self_propagation: {
    available: boolean;
    mints_licence: boolean;
    note: string;
  };
}

export interface PropagatorTerms {
  id: number;
  cultivar_id: number;
  propagator_org_id: number;
  propagator_name: string;
  licensor_org_id: number;
  licensor_name: string;
  propagator_kind: 'nursery' | 'self_propagating_orchard';
  per_tree_fee: string | number | null;
  currency: string;
  conditions: string | null;
  status: 'active' | 'suspended' | 'withdrawn';
  valid_from: string | null;
  valid_to: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PropagatorTermsInput {
  propagator_org_id: number;
  propagator_kind?: 'nursery' | 'self_propagating_orchard';
  per_tree_fee?: number | null;
  currency?: string;
  conditions?: string;
  status?: 'active' | 'suspended' | 'withdrawn';
  valid_from?: string | null;
  valid_to?: string | null;
  notes?: string;
}

/** A nursery's answer. Never overwrites the orchard's claim — both are kept. */
export interface SupplyVerification {
  id: number;
  cultivar_area_id: number;
  nursery_org_id: number;
  nursery_name?: string;
  claimed_tree_count: number | null;
  claimed_planting_year: number | null;
  status: 'confirmed' | 'corrected' | 'disputed';
  verified_tree_count: number | null;
  verified_planting_year: number | null;
  notes: string | null;
  verified_at: string;
}

/**
 * A nursery's own terms of sale.
 *
 * Supplying the trees is a contract between the nursery and the grower.
 * Factree carries the document and is not a party to it.
 */
export interface NurseryTermsDocument {
  id: number;
  nursery_org_id: number;
  title: string;
  version: string | null;
  original_filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  status: 'active' | 'superseded';
  created_at: string;
  updated_at: string;
}

/**
 * What a nursery is offering for a season.
 *
 * `unit_price` and the deposit figures are the nursery's own and are only ever
 * returned to the organisation that set them. A grower sees one combined total
 * with the licence fees folded in.
 */
export interface NurseryOffer {
  id: number;
  nursery_org_id: number;
  cultivar_id: number;
  cultivar_name: string;
  cultivar_trade_name: string | null;
  cultivar_protection_status: string | null;
  rootstock_cultivar_id: number | null;
  rootstock_name: string | null;
  tree_type: string | null;
  season_year: number;
  order_window_start: string | null;
  order_window_end: string | null;
  expected_delivery_start: string | null;
  expected_delivery_end: string | null;
  unit_price: string | number;
  currency: string;
  min_order_quantity: number;
  available_quantity: number | null;
  deposit_type: 'none' | 'percent' | 'fixed';
  deposit_value: string | number | null;
  payment_terms_days: number | null;
  payment_schedule: string | null;
  terms_document_id: number | null;
  terms_title: string | null;
  terms_version: string | null;
  /** The nursery handles its conditions of sale outside Factree, and said so. */
  terms_handled_externally: boolean;
  external_terms_acknowledged_at: string | null;
  external_terms_acknowledged_by_user_id: number | null;
  /** The exact wording acknowledged, as it stood at the time. */
  external_terms_acknowledgement_text: string | null;
  material_sources?: MaterialSource[];
  status: 'draft' | 'active' | 'closed' | 'withdrawn';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface NurseryOfferInput {
  id?: number;
  cultivar_id: number;
  rootstock_cultivar_id?: number | null;
  tree_type?: string | null;
  season_year: number;
  order_window_start?: string | null;
  order_window_end?: string | null;
  expected_delivery_start?: string | null;
  expected_delivery_end?: string | null;
  unit_price: number;
  currency?: string;
  min_order_quantity?: number;
  available_quantity?: number | null;
  deposit_type?: 'none' | 'percent' | 'fixed';
  deposit_value?: number | null;
  payment_terms_days?: number | null;
  payment_schedule?: string | null;
  terms_document_id?: number | null;
  terms_handled_externally?: boolean;
  /** The tick itself. The wording it acknowledges is stamped by the server. */
  acknowledge_external_terms?: boolean;
  /** Omitted leaves the declared sources as they are. */
  material_sources?: MaterialSourceInput[];
  status?: 'draft' | 'active' | 'closed' | 'withdrawn';
  notes?: string | null;
}


// =============================================================================
// CULTIVAR SERVICE
// =============================================================================

class CultivarService {

  // --- Crop Types ---

  async getCropTypes(): Promise<ApiResponse<CropType[]>> {
    return apiService.get('/cultivar/crop-types');
  }

  async createCropType(name: string, description?: string, parentId?: number | null): Promise<ApiResponse<CropType>> {
    return apiService.post('/cultivar/crop-types', { name, description, parent_id: parentId ?? null });
  }

  async updateCropType(
    id: number,
    data: { name?: string; description?: string | null; parent_id?: number | null }
  ): Promise<ApiResponse<CropType>> {
    return apiService.put(`/cultivar/crop-types/${id}`, data);
  }

  async deleteCropType(id: number): Promise<ApiResponse<{ id: number }>> {
    return apiService.delete(`/cultivar/crop-types/${id}`);
  }

  // --- Stakeholder Types ---

  async getStakeholderTypes(): Promise<ApiResponse<StakeholderType[]>> {
    return apiService.get('/cultivar/stakeholder-types');
  }

  async addStakeholderType(stakeholder_type: string): Promise<ApiResponse<StakeholderType>> {
    return apiService.post('/cultivar/stakeholder-types', { stakeholder_type });
  }

  async removeStakeholderType(type: string): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/stakeholder-types/${type}`);
  }

  // --- Super admin: manage other org's stakeholder types ---

  async getStakeholderTypesForOrg(orgId: number): Promise<ApiResponse<StakeholderType[]>> {
    return apiService.get(`/cultivar/stakeholder-types?org_id=${orgId}`);
  }

  async addStakeholderTypeForOrg(orgId: number, stakeholder_type: string): Promise<ApiResponse<StakeholderType>> {
    return apiService.post('/cultivar/stakeholder-types', { stakeholder_type, org_id: orgId });
  }

  async removeStakeholderTypeForOrg(orgId: number, type: string): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/stakeholder-types/${type}?org_id=${orgId}`);
  }

  // --- Cultivar Registry ---

  async searchCultivars(params: {
    search?: string; crop_type_id?: number; status?: string;
    breeder_org_id?: number; protection_status?: string;
    cultivar_role?: 'scion' | 'rootstock';
    program_id?: number;
    page?: number; limit?: number;
  } = {}): Promise<ApiResponse<{ cultivars: Cultivar[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.crop_type_id) queryParams.append('crop_type_id', String(params.crop_type_id));
    if (params.cultivar_role) queryParams.append('cultivar_role', params.cultivar_role);
    if (params.status) queryParams.append('status', params.status);
    if (params.breeder_org_id) queryParams.append('breeder_org_id', String(params.breeder_org_id));
    if (params.protection_status) queryParams.append('protection_status', params.protection_status);
    if (params.program_id) queryParams.append('program_id', String(params.program_id));
    if (params.page) queryParams.append('page', String(params.page));
    if (params.limit) queryParams.append('limit', String(params.limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/registry${qs ? `?${qs}` : ''}`);
  }

  // --- Cultivar Programs ---

  async listPrograms(filters?: {
    organisation_id?: number;
    active_only?: boolean;
    program_type?: CultivarProgramType;
    search?: string;
    /** Only the caller's own org's programs — for anything that edits or attaches. */
    mine?: boolean;
  }): Promise<ApiResponse<CultivarProgram[]>> {
    const q = new URLSearchParams();
    if (filters?.mine) q.append('mine', 'true');
    if (filters?.organisation_id) q.append('organisation_id', String(filters.organisation_id));
    if (filters?.active_only) q.append('active_only', 'true');
    if (filters?.program_type) q.append('program_type', filters.program_type);
    if (filters?.search) q.append('search', filters.search);
    const qs = q.toString();
    return apiService.get(`/cultivar/programs${qs ? `?${qs}` : ''}`);
  }

  async getProgram(id: number): Promise<ApiResponse<CultivarProgram>> {
    return apiService.get(`/cultivar/programs/${id}`);
  }

  async createProgram(data: ProgramFormData): Promise<ApiResponse<CultivarProgram>> {
    return apiService.post('/cultivar/programs', data);
  }

  async updateProgram(id: number, data: Partial<ProgramFormData>): Promise<ApiResponse<CultivarProgram>> {
    return apiService.put(`/cultivar/programs/${id}`, data);
  }

  async deleteProgram(id: number): Promise<ApiResponse<CultivarProgram>> {
    return apiService.delete(`/cultivar/programs/${id}`);
  }

  async getCultivar(id: number): Promise<ApiResponse<Cultivar>> {
    return apiService.get(`/cultivar/registry/${id}`);
  }

  async getCultivarPrograms(cultivarId: number): Promise<ApiResponse<CultivarProgram[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/programs`);
  }

  async addCultivarProgram(cultivarId: number, programId: number): Promise<ApiResponse<CultivarProgram[]>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/programs/${programId}`, {});
  }

  async removeCultivarProgram(cultivarId: number, programId: number): Promise<ApiResponse<CultivarProgram[]>> {
    return apiService.delete(`/cultivar/registry/${cultivarId}/programs/${programId}`);
  }

  async createCultivar(data: Record<string, unknown>): Promise<ApiResponse<Cultivar>> {
    return apiService.post('/cultivar/registry', data);
  }

  async updateCultivar(id: number, data: Record<string, unknown>): Promise<ApiResponse<Cultivar>> {
    return apiService.put(`/cultivar/registry/${id}`, data);
  }

  async setWebsitePublish(id: number, showOnWebsite: boolean): Promise<ApiResponse<Cultivar>> {
    return apiService.patch(`/cultivar/registry/${id}/website-publish`, { show_on_website: showOnWebsite });
  }

  /**
   * AI research for the cultivar's description and talking points. A proposal
   * to review: nothing is saved. Cultivar Admin seat, own cultivar. The server
   * stops itself at 240 s, so the wait here is a little longer than that.
   */
  async researchCultivarInfo(id: number): Promise<ApiResponse<CultivarInfoProposal>> {
    return apiService.post(`/cultivar/registry/${id}/info/research`, {}, undefined, { timeout: 270_000 });
  }

  /** Save the reviewed description, tagline, talking points and whether the website shows them. */
  async saveCultivarInfo(id: number, data: CultivarInfoSave): Promise<ApiResponse<CultivarInfo>> {
    return apiService.put(`/cultivar/registry/${id}/info`, data);
  }

  // --- Sales notes (the organisation's own, private to it) ---

  async listSalesNotes(cultivarId: number): Promise<ApiResponse<CultivarSalesNote[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/sales-notes`);
  }

  async addSalesNote(cultivarId: number, body: string): Promise<ApiResponse<CultivarSalesNote>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/sales-notes`, { body });
  }

  async updateSalesNote(cultivarId: number, noteId: number, body: string): Promise<ApiResponse<CultivarSalesNote>> {
    return apiService.put(`/cultivar/registry/${cultivarId}/sales-notes/${noteId}`, { body });
  }

  async deleteSalesNote(cultivarId: number, noteId: number): Promise<ApiResponse<{ id: number }>> {
    return apiService.delete(`/cultivar/registry/${cultivarId}/sales-notes/${noteId}`);
  }

  async deleteCultivar(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/registry/${id}`);
  }

  async permanentlyDeleteCultivar(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/registry/${id}/permanent`);
  }

  // --- Cultivar Protections ---

  async getProtections(cultivarId: number): Promise<ApiResponse<CultivarProtection[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/protections`);
  }

  async addProtection(cultivarId: number, data: object): Promise<ApiResponse<CultivarProtection>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/protections`, data);
  }

  async bulkUpsertProtections(cultivarId: number, protections: object[]): Promise<ApiResponse<CultivarProtection[]>> {
    return apiService.put(`/cultivar/registry/${cultivarId}/protections/bulk`, { protections });
  }

  async deleteProtection(cultivarId: number, protectionId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/registry/${cultivarId}/protections/${protectionId}`);
  }

  // --- Regional Licenses ---

  async getLicensesForCultivar(cultivarId: number, filters?: { status?: string; license_tier?: string }): Promise<ApiResponse<RegionalLicense[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.license_tier) queryParams.append('license_tier', filters.license_tier);
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/registry/${cultivarId}/licenses${qs ? `?${qs}` : ''}`);
  }

  async getMyLicenses(filters?: {
    role?: string; status?: string; license_tier?: string;
    cultivar_id?: number; program_id?: number; page?: number; limit?: number;
  }): Promise<ApiResponse<{ licenses: RegionalLicense[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (filters?.role) queryParams.append('role', filters.role);
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.license_tier) queryParams.append('license_tier', filters.license_tier);
    if (filters?.cultivar_id) queryParams.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.program_id) queryParams.append('program_id', String(filters.program_id));
    if (filters?.page) queryParams.append('page', String(filters.page));
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/licenses${qs ? `?${qs}` : ''}`);
  }

  async getPendingConfirmations(): Promise<ApiResponse<RegionalLicense[]>> {
    return apiService.get('/cultivar/licenses/pending');
  }

  async getLicense(licenseId: number): Promise<ApiResponse<RegionalLicense>> {
    return apiService.get(`/cultivar/licenses/${licenseId}`);
  }

  /**
   * Licensee declines a pending licence before signing. After signing the
   * licensee must request termination from the licensor.
   */
  async rejectLicense(licenseId: number, reason?: string): Promise<ApiResponse<void>> {
    return apiService.post(`/cultivar/licenses/${licenseId}/reject`, { reason });
  }

  /**
   * Licensor suspends an active licence. Requires both an internal audit
   * reason (full detail, retained in the audit log) and a brief public reason
   * (shown to the licensee on screen and in email).
   */
  async suspendLicense(
    licenseId: number,
    reasons: { internal: string; public: string }
  ): Promise<ApiResponse<RegionalLicense>> {
    return apiService.post(`/cultivar/licenses/${licenseId}/suspend`, reasons);
  }

  /**
   * Licensor terminates a licence (from active or suspended). Irreversible.
   */
  async terminateLicense(
    licenseId: number,
    reasons: { internal: string; public: string }
  ): Promise<ApiResponse<RegionalLicense>> {
    return apiService.post(`/cultivar/licenses/${licenseId}/terminate`, reasons);
  }

  // --- Production Declarations ---

  async getDeclarationsForLicense(licenseId: number, filters?: { season_year?: number; status?: string }): Promise<ApiResponse<ProductionDeclaration[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.season_year) queryParams.append('season_year', String(filters.season_year));
    if (filters?.status) queryParams.append('status', filters.status);
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/licenses/${licenseId}/declarations${qs ? `?${qs}` : ''}`);
  }

  async getMyDeclarations(filters?: {
    season_year?: number; status?: string; cultivar_id?: number;
    page?: number; limit?: number;
  }): Promise<ApiResponse<{ declarations: ProductionDeclaration[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (filters?.season_year) queryParams.append('season_year', String(filters.season_year));
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.cultivar_id) queryParams.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.page) queryParams.append('page', String(filters.page));
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/declarations${qs ? `?${qs}` : ''}`);
  }

  async getDeclaration(id: number): Promise<ApiResponse<ProductionDeclaration>> {
    return apiService.get(`/cultivar/declarations/${id}`);
  }

  async submitDeclaration(licenseId: number, data: Record<string, unknown>): Promise<ApiResponse<ProductionDeclaration>> {
    return apiService.post(`/cultivar/licenses/${licenseId}/declarations`, data);
  }

  async updateDeclaration(id: number, data: Record<string, unknown>): Promise<ApiResponse<ProductionDeclaration>> {
    return apiService.put(`/cultivar/declarations/${id}`, data);
  }

  async acknowledgeDeclaration(id: number, notes?: string): Promise<ApiResponse<ProductionDeclaration>> {
    return apiService.post(`/cultivar/declarations/${id}/acknowledge`, { notes });
  }

  async queryDeclaration(id: number, message: string): Promise<ApiResponse<ProductionDeclaration>> {
    return apiService.post(`/cultivar/declarations/${id}/query`, { message });
  }

  async rejectDeclaration(id: number, reason: string): Promise<ApiResponse<ProductionDeclaration>> {
    return apiService.post(`/cultivar/declarations/${id}/reject`, { reason });
  }

  // --- Entitlement Certificates ---

  async generateCertificate(declarationId: number): Promise<ApiResponse<EntitlementCertificate>> {
    return apiService.post(`/cultivar/declarations/${declarationId}/certificate`, {});
  }

  async getMyCertificates(filters?: { status?: string; page?: number; limit?: number }): Promise<ApiResponse<{ certificates: EntitlementCertificate[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.page) queryParams.append('page', String(filters.page));
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/certificates${qs ? `?${qs}` : ''}`);
  }

  async getCertificate(id: number): Promise<ApiResponse<EntitlementCertificate>> {
    return apiService.get(`/cultivar/certificates/${id}`);
  }

  async downloadCertificatePDF(id: number): Promise<Blob> {
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/certificates/${id}/pdf`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) throw new Error('Failed to download PDF');
    return response.blob();
  }

  async revokeCertificate(id: number, reason: string): Promise<ApiResponse<void>> {
    return apiService.post(`/cultivar/certificates/${id}/revoke`, { reason });
  }

  async verifyCertificate(token: string): Promise<ApiResponse<CertificateVerification>> {
    return apiService.get(`/cultivar/verify/${token}`);
  }

  // --- Nursery Supply ---

  async getNurseriesForCultivar(cultivarId: number): Promise<ApiResponse<NurseryCultivar[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/nurseries`);
  }

  async getMyNurseryListings(): Promise<ApiResponse<NurseryCultivar[]>> {
    return apiService.get('/cultivar/nurseries');
  }

  async registerAsSupplier(cultivarId: number, data: Record<string, unknown>): Promise<ApiResponse<NurseryCultivar>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/nurseries`, data);
  }

  async updateNurseryListing(id: number, data: Record<string, unknown>): Promise<ApiResponse<NurseryCultivar>> {
    return apiService.put(`/cultivar/nurseries/${id}`, data);
  }

  async removeNurseryListing(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/nurseries/${id}`);
  }

  // --- Compliance Documents ---

  async getDocuments(filters?: {
    document_type?: string; status?: string;
    related_entity_type?: string; related_entity_id?: number;
    page?: number; limit?: number;
  }): Promise<ApiResponse<{ documents: ComplianceDocument[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (filters?.document_type) queryParams.append('document_type', filters.document_type);
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.related_entity_type) queryParams.append('related_entity_type', filters.related_entity_type);
    if (filters?.related_entity_id) queryParams.append('related_entity_id', String(filters.related_entity_id));
    if (filters?.page) queryParams.append('page', String(filters.page));
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/documents${qs ? `?${qs}` : ''}`);
  }

  async uploadDocument(formData: FormData): Promise<ApiResponse<ComplianceDocument>> {
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/documents`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error?.error?.message || 'Upload failed');
    }
    return response.json();
  }

  async getDocument(id: number): Promise<ApiResponse<ComplianceDocument>> {
    return apiService.get(`/cultivar/documents/${id}`);
  }

  async updateDocument(id: number, data: Record<string, unknown>): Promise<ApiResponse<ComplianceDocument>> {
    return apiService.put(`/cultivar/documents/${id}`, data);
  }

  async deleteDocument(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/documents/${id}`);
  }

  // --- Management Guides ---

  async getGuidesForCultivar(cultivarId: number): Promise<ApiResponse<ManagementGuide[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/guides`);
  }

  async getAccessibleGuides(filters?: {
    category?: string; cultivar_id?: number; page?: number; limit?: number;
  }): Promise<ApiResponse<{ guides: ManagementGuide[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (filters?.category) queryParams.append('category', filters.category);
    if (filters?.cultivar_id) queryParams.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.page) queryParams.append('page', String(filters.page));
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/guides${qs ? `?${qs}` : ''}`);
  }

  async getGuide(id: number): Promise<ApiResponse<ManagementGuide>> {
    return apiService.get(`/cultivar/guides/${id}`);
  }

  async createGuide(cultivarId: number, data: Record<string, unknown>): Promise<ApiResponse<ManagementGuide>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/guides`, data);
  }

  async updateGuide(id: number, data: Record<string, unknown>): Promise<ApiResponse<ManagementGuide>> {
    return apiService.put(`/cultivar/guides/${id}`, data);
  }

  async deleteGuide(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/guides/${id}`);
  }

  // --- Compliance Dashboard & Alerts ---

  async getComplianceDashboard(): Promise<ApiResponse<ComplianceDashboard>> {
    return apiService.get('/cultivar/compliance/dashboard');
  }

  async getAlerts(filters?: {
    status?: string; severity?: string; alert_type?: string;
    page?: number; limit?: number;
  }): Promise<ApiResponse<{ alerts: ComplianceAlert[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.severity) queryParams.append('severity', filters.severity);
    if (filters?.alert_type) queryParams.append('alert_type', filters.alert_type);
    if (filters?.page) queryParams.append('page', String(filters.page));
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/compliance/alerts${qs ? `?${qs}` : ''}`);
  }

  async acknowledgeAlert(id: number): Promise<ApiResponse<ComplianceAlert>> {
    return apiService.patch(`/cultivar/compliance/alerts/${id}`, { action: 'acknowledge' });
  }

  async dismissAlert(id: number): Promise<ApiResponse<ComplianceAlert>> {
    return apiService.patch(`/cultivar/compliance/alerts/${id}`, { action: 'dismiss' });
  }

  async generateAlerts(): Promise<ApiResponse<{ generated: number; alerts: ComplianceAlert[] }>> {
    return apiService.post('/cultivar/compliance/generate-alerts', {});
  }

  // --- Audit Log ---

  async getAuditHistory(entityType: string, entityId: number, page?: number, limit?: number): Promise<ApiResponse<{ entries: AuditLogEntry[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', String(page));
    if (limit) queryParams.append('limit', String(limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/audit/${entityType}/${entityId}${qs ? `?${qs}` : ''}`);
  }

  // =========================================================================
  // CULTIVAR LICENSORS (region-scoped)
  // =========================================================================

  async getCultivarLicensors(cultivarId: number): Promise<ApiResponse<any[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/licensors`);
  }

  async addCultivarLicensor(cultivarId: number, data: {
    licensor_org_id: number;
    region?: string | null;
    country?: string | null;
    notes?: string | null;
  }): Promise<ApiResponse<any>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/licensors`, data);
  }

  async removeCultivarLicensor(cultivarId: number, licensorRowId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/registry/${cultivarId}/licensors/${licensorRowId}`);
  }

  // =========================================================================
  // LICENSING WORKFLOW: TEMPLATES, SIGNING, ORDERS, LICENSOR DASHBOARD
  // =========================================================================

  /**
   * Send an agreement. The contract is named by family (a master takes its
   * group's and names none); the server sends that family's published version.
   * `expected_content_hash` is the fingerprint of the previewed text — if the
   * text has moved since, the server refuses rather than send unread terms.
   */
  async issueLicenseFromTemplate(data: IssueLicenseInput & {
    notify_user_id: number;
    expected_content_hash: string;
  }): Promise<ApiResponse<RegionalLicense>> {
    return apiService.post('/cultivar/licenses/issue-from-template', data);
  }

  /** Exactly what issuing with these details would send. Writes nothing. */
  async previewLicense(data: IssueLicenseInput): Promise<ApiResponse<LicensePreview>> {
    return apiService.post('/cultivar/licenses/preview', data);
  }

  async getOrgCultivarContacts(orgId: number): Promise<ApiResponse<Array<{
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  }>>> {
    return apiService.get(`/cultivar/organisations/${orgId}/cultivar-contacts`);
  }

  async getLicenseAgreement(licenseId: number): Promise<ApiResponse<any>> {
    return apiService.get(`/cultivar/licenses/${licenseId}/agreement`);
  }

  /**
   * `content_hash` is the fingerprint the page displayed; the server refuses
   * the signature if the agreement on record is not that text.
   */
  async signLicenseElectronic(licenseId: number, signer_name: string, content_hash?: string | null): Promise<ApiResponse<any>> {
    return apiService.post(`/cultivar/licenses/${licenseId}/sign`, { signer_name, agree: true, content_hash: content_hash ?? null });
  }

  async uploadSignedLicense(licenseId: number, file: File, signer_name?: string, content_hash?: string | null): Promise<ApiResponse<any>> {
    const fd = new FormData();
    fd.append('file', file);
    if (signer_name) fd.append('signer_name', signer_name);
    if (content_hash) fd.append('content_hash', content_hash);
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/licenses/${licenseId}/upload-signed`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      // Carry the server's code, so a refusal because the text changed or the
      // agreement was replaced reads the same as it does for e-signing.
      const apiErr = new ApiError(err?.error?.message || 'Upload failed', response.status);
      apiErr.code = err?.error?.code;
      throw apiErr;
    }
    return response.json();
  }

  async downloadSignedLicensePdf(licenseId: number): Promise<Blob> {
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/licenses/${licenseId}/signed-pdf`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    if (!response.ok) throw new Error('Failed to download signed PDF');
    return response.blob();
  }

  async getOrchardAllowance(cultivarId: number, excludeAreaId?: number): Promise<ApiResponse<{
    license_id: number; max_trees: number; tolerance_pct: number;
    cap: number; used: number; remaining: number;
  } | null>> {
    const q = new URLSearchParams({ cultivar_id: String(cultivarId) });
    if (excludeAreaId) q.append('exclude_area_id', String(excludeAreaId));
    return apiService.get(`/cultivar/licenses/orchard-allowance?${q.toString()}`);
  }

  /**
   * Nurseries allowed to supply trees of this cultivar — those holding an
   * active propagation licence for it. The only valid answers to "supplying
   * nursery" on a planting licence.
   */
  async listAuthorisedNurseries(cultivarId: number): Promise<ApiResponse<AuthorisedNursery[]>> {
    return apiService.get(`/cultivar/licenses/authorised-nurseries?cultivar_id=${cultivarId}`);
  }

  // --- Nursery offers, and the nursery's own terms of sale -----------------
  //
  // `unit_price` is the nursery's own number and these endpoints only ever
  // serve the organisation that set it. A grower sees one combined total with
  // the licence fees folded in — never the parts. That opacity is deliberate.

  async listNurseryOffers(filters?: {
    season_year?: number;
    status?: string;
  }): Promise<ApiResponse<NurseryOffer[]>> {
    const q = new URLSearchParams();
    if (filters?.season_year) q.append('season_year', String(filters.season_year));
    if (filters?.status) q.append('status', filters.status);
    const qs = q.toString();
    return apiService.get(`/cultivar/nursery-offers${qs ? `?${qs}` : ''}`);
  }

  /** Create or update. An offer cannot go active without terms attached. */
  async saveNurseryOffer(input: NurseryOfferInput): Promise<ApiResponse<NurseryOffer>> {
    return apiService.put('/cultivar/nursery-offers', input);
  }

  async withdrawNurseryOffer(id: number): Promise<ApiResponse<NurseryOffer>> {
    return apiService.delete(`/cultivar/nursery-offers/${id}`);
  }

  async listNurseryTermsDocuments(): Promise<ApiResponse<NurseryTermsDocument[]>> {
    return apiService.get('/cultivar/nursery-terms-documents');
  }

  /**
   * Upload the nursery's own terms of sale.
   *
   * Multipart, so it goes via fetch rather than the JSON axios instance whose
   * default Content-Type would strip the boundary.
   */
  async uploadNurseryTermsDocument(
    file: File,
    title: string,
    version?: string
  ): Promise<ApiResponse<NurseryTermsDocument>> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    if (version) fd.append('version', version);
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/nursery-terms-documents`,
      { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.error?.message || 'Upload failed');
    }
    return response.json();
  }

  /** Superseded, never deleted — an order signed under these terms still needs them. */
  async supersedeNurseryTermsDocument(id: number): Promise<ApiResponse<NurseryTermsDocument>> {
    return apiService.delete(`/cultivar/nursery-terms-documents/${id}`);
  }

  /** The route needs the bearer token, so it cannot be a plain href. */
  async openNurseryTermsDocument(doc: Pick<NurseryTermsDocument, 'id' | 'original_filename'>): Promise<void> {
    await openDocument(`/cultivar/nursery-terms-documents/${doc.id}/file`, doc.original_filename || `terms-${doc.id}`);
  }

  /**
   * The wording a nursery acknowledges when it handles its terms of sale
   * outside Factree. Fetched, not hard-coded, because the server stamps this
   * exact text onto the offer — the page has to show what gets stored.
   */
  async getExternalTermsAcknowledgement(): Promise<ApiResponse<{ text: string }>> {
    return apiService.get('/cultivar/nursery-offers/external-terms-acknowledgement');
  }

  /** Every declared propagation-material source for a cultivar. Rights holder only. */
  async listMaterialSources(cultivarId: number): Promise<ApiResponse<DeclaredMaterialSource[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/material-sources`);
  }

  // --- Sublicensing --------------------------------------------------------

  async listSublicenceGroups(includeClosed = false): Promise<ApiResponse<SublicenceGroup[]>> {
    const qs = includeClosed ? '?include_closed=true' : '';
    return apiService.get(`/cultivar/sublicence-groups${qs}`);
  }

  async createSublicenceGroup(
    input: {
      code: string; name?: string; description?: string;
      contract_family_id?: number | null; schedule_contract_family_id?: number | null;
    }
  ): Promise<ApiResponse<SublicenceGroup>> {
    return apiService.post('/cultivar/sublicence-groups', input);
  }

  async updateSublicenceGroup(
    id: number,
    input: {
      name?: string; description?: string; status?: 'active' | 'closed';
      contract_family_id?: number | null; schedule_contract_family_id?: number | null;
    }
  ): Promise<ApiResponse<SublicenceGroup>> {
    return apiService.put(`/cultivar/sublicence-groups/${id}`, input);
  }

  /** Who is admitted to a group, and under which master. */
  async listGroupNurseries(groupId: number): Promise<ApiResponse<GroupNursery[]>> {
    return apiService.get(`/cultivar/sublicence-groups/${groupId}/nurseries`);
  }

  async getCultivarSublicensing(cultivarId: number): Promise<ApiResponse<CultivarSublicensing>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/sublicensing`);
  }

  /** `nursery_org_ids` is the complete set, not a delta. */
  async setCultivarSublicensing(
    cultivarId: number,
    input: { is_sublicensable?: boolean; nursery_org_ids?: number[] }
  ): Promise<ApiResponse<CultivarSublicensing>> {
    return apiService.put(`/cultivar/registry/${cultivarId}/sublicensing`, input);
  }

  /**
   * Exactly what the public site will offer. Same resolver the public endpoint
   * runs, so a preview cannot drift from the real thing.
   */
  async getCultivarSuppliers(cultivarId: number): Promise<ApiResponse<CultivarSuppliers>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/suppliers`);
  }

  // --- Propagator terms ----------------------------------------------------

  /** Who may propagate this cultivar, and on what terms. */
  async listPropagatorTerms(cultivarId: number): Promise<ApiResponse<PropagatorTerms[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/propagator-terms`);
  }

  /** Admit a propagator, or change the terms they are on. Licensor only. */
  async setPropagatorTerms(
    cultivarId: number,
    input: PropagatorTermsInput
  ): Promise<ApiResponse<PropagatorTerms>> {
    return apiService.put(`/cultivar/registry/${cultivarId}/propagator-terms`, input);
  }

  /** Withdraw eligibility. Kept as a row, not deleted — orders referenced it. */
  async withdrawPropagatorTerms(
    cultivarId: number,
    propagatorOrgId: number
  ): Promise<ApiResponse<PropagatorTerms>> {
    return apiService.delete(`/cultivar/registry/${cultivarId}/propagator-terms/${propagatorOrgId}`);
  }

  /** What my organisation may propagate, and at what rate. */
  async listMyPropagatorTerms(): Promise<ApiResponse<PropagatorTerms[]>> {
    return apiService.get('/cultivar/my-propagator-terms');
  }

  // --- Supply verification -------------------------------------------------
  //
  // An orchard mapping a block planted before Factree names the nursery that
  // grew the trees. These are that nursery's side of it. Verifying raises
  // confidence in a number; it grants nothing and creates no licence.

  /** Mapped areas naming my organisation as supplier. */
  async listSupplyClaims(filters?: {
    status?: 'unanswered' | 'confirmed' | 'corrected' | 'disputed';
    cultivar_id?: number;
    orchard_org_id?: number;
  }): Promise<ApiResponse<SupplyClaim[]>> {
    const q = new URLSearchParams();
    if (filters?.status) q.append('status', filters.status);
    if (filters?.cultivar_id) q.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.orchard_org_id) q.append('orchard_org_id', String(filters.orchard_org_id));
    const qs = q.toString();
    return apiService.get(`/cultivar/supply-claims${qs ? `?${qs}` : ''}`);
  }

  /** Answer one claim. `corrected` must carry a count, a year, or both. */
  async verifySupplyClaim(
    cultivarAreaId: number,
    payload: {
      status: 'confirmed' | 'corrected' | 'disputed';
      verified_tree_count?: number | null;
      verified_planting_year?: number | null;
      notes?: string;
    }
  ): Promise<ApiResponse<SupplyVerification>> {
    return apiService.post(`/cultivar/supply-claims/${cultivarAreaId}/verify`, payload);
  }

  /** Every nursery's answer on one area. */
  async listSupplyVerifications(cultivarAreaId: number): Promise<ApiResponse<SupplyVerification[]>> {
    return apiService.get(`/cultivar/cultivar-areas/${cultivarAreaId}/supply-verifications`);
  }

  async listOrders(filters?: { role?: 'orchard' | 'nursery' | 'licensor'; status?: string; cultivar_id?: number }): Promise<ApiResponse<any[]>> {
    const q = new URLSearchParams();
    if (filters?.role) q.append('role', filters.role);
    if (filters?.status) q.append('status', filters.status);
    if (filters?.cultivar_id) q.append('cultivar_id', String(filters.cultivar_id));
    const qs = q.toString();
    return apiService.get(`/cultivar/orders${qs ? `?${qs}` : ''}`);
  }

  async createOrder(data: {
    nursery_org_id: number;
    cultivar_id: number;
    qty_trees: number;
    orchard_license_id?: number | null;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    return apiService.post('/cultivar/orders', data);
  }

  async updateOrderStatus(id: number, status: string): Promise<ApiResponse<any>> {
    return apiService.patch(`/cultivar/orders/${id}/status`, { status });
  }

  async getLicensorDashboard(): Promise<ApiResponse<{
    summary: Record<string, number>;
    contracts: any[];
    orders: any[];
  }>> {
    return apiService.get('/cultivar/licensor/dashboard');
  }

  async getLicensorMapData(farmId?: number): Promise<ApiResponse<{ cultivar_areas: any[] }>> {
    const qs = farmId ? `?farm_id=${farmId}` : '';
    return apiService.get(`/cultivar/licensor/map-data${qs}`);
  }

  async verifyLicense(token: string): Promise<ApiResponse<any>> {
    return apiService.get(`/cultivar/verify-license/${token}`);
  }

  async getOrgAuditFeed(page?: number, limit?: number): Promise<ApiResponse<{ entries: AuditLogEntry[]; pagination: Pagination }>> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', String(page));
    if (limit) queryParams.append('limit', String(limit));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/audit${qs ? `?${qs}` : ''}`);
  }

  // ===========================================================================
  // ATTRIBUTE DEFINITIONS (globals + per-org custom)
  // ===========================================================================

  async listAttributeDefinitions(includeInactive = false): Promise<ApiResponse<AttributeDefinition[]>> {
    const qs = includeInactive ? '?include_inactive=true' : '';
    return apiService.get(`/cultivar/attribute-definitions${qs}`);
  }

  async createAttributeDefinition(data: Partial<AttributeDefinition> & { slug: string; display_name: string; category: string; data_type: AttributeDataType }): Promise<ApiResponse<AttributeDefinition>> {
    return apiService.post('/cultivar/attribute-definitions', data);
  }

  async updateAttributeDefinition(id: number, data: Partial<AttributeDefinition>): Promise<ApiResponse<AttributeDefinition>> {
    return apiService.put(`/cultivar/attribute-definitions/${id}`, data);
  }

  async deactivateAttributeDefinition(id: number): Promise<ApiResponse<AttributeDefinition>> {
    return apiService.delete(`/cultivar/attribute-definitions/${id}`);
  }

  // ===========================================================================
  // ATTRIBUTE VALUES (observations)
  // ===========================================================================

  async listAttributeValues(cultivarId: number, filters?: { attribute_id?: number; observer_org_id?: number; season_year?: number }): Promise<ApiResponse<AttributeValue[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.attribute_id) queryParams.append('attribute_id', String(filters.attribute_id));
    if (filters?.observer_org_id) queryParams.append('observer_org_id', String(filters.observer_org_id));
    if (filters?.season_year) queryParams.append('season_year', String(filters.season_year));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/registry/${cultivarId}/attribute-values${qs ? `?${qs}` : ''}`);
  }

  async createAttributeValue(cultivarId: number, data: { attribute_id: number; value: unknown; season_year?: number | null; latitude?: number | null; longitude?: number | null; farm_id?: number | null; growing_region_id?: number | null; source_notes?: string | null }): Promise<ApiResponse<AttributeValue>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/attribute-values`, data);
  }

  async deleteAttributeValue(valueId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/attribute-values/${valueId}`);
  }

  // ===========================================================================
  // EVALUATION SESSIONS
  // ===========================================================================

  async listEvaluations(cultivarId: number, filters?: { season_year?: number; observer_org_id?: number; status?: string; limit?: number; normalise_to?: Hemisphere }): Promise<ApiResponse<CultivarEvaluation[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.season_year) queryParams.append('season_year', String(filters.season_year));
    if (filters?.observer_org_id) queryParams.append('observer_org_id', String(filters.observer_org_id));
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    if (filters?.normalise_to) queryParams.append('normalise_to', filters.normalise_to);
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/registry/${cultivarId}/evaluations${qs ? `?${qs}` : ''}`);
  }

  /** Recorded harvest dates per hemisphere — labels the comparison toggle. */
  async getHarvestReferences(cultivarId: number): Promise<ApiResponse<HarvestReferences>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/harvest-references`);
  }

  async createEvaluation(cultivarId: number, data: EvaluationInput): Promise<ApiResponse<CultivarEvaluation>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/evaluations`, data);
  }

  async getEvaluation(evaluationId: number): Promise<ApiResponse<CultivarEvaluation>> {
    return apiService.get(`/cultivar/evaluations/${evaluationId}`);
  }

  async updateEvaluation(evaluationId: number, data: Partial<Pick<CultivarEvaluation, 'summary' | 'conditions_notes' | 'overall_score' | 'location_label' | 'season_stage' | 'status'>>): Promise<ApiResponse<CultivarEvaluation>> {
    return apiService.patch(`/cultivar/evaluations/${evaluationId}`, data);
  }

  async deleteEvaluation(evaluationId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/evaluations/${evaluationId}`);
  }

  /**
   * Live "62 days before harvest" readout for a date the user is picking.
   * Pass the captured latitude so the harvest reference is taken from the
   * right hemisphere — without it a southern visit may be measured against a
   * northern harvest and be out by half a year.
   */
  async getSeasonPosition(cultivarId: number, evaluatedOn: string, opts?: { latitude?: number | null; hemisphere?: Hemisphere | null }): Promise<ApiResponse<SeasonPosition>> {
    const queryParams = new URLSearchParams({ evaluated_on: evaluatedOn });
    if (opts?.latitude != null) queryParams.append('latitude', String(opts.latitude));
    else if (opts?.hemisphere) queryParams.append('hemisphere', opts.hemisphere);
    return apiService.get(`/cultivar/registry/${cultivarId}/season-position?${queryParams.toString()}`);
  }

  /**
   * Upload one photo against an evaluation. Raw fetch + FormData rather than
   * axios, matching uploadCultivarMedia — axios forces a JSON content-type.
   * `client_uuid` makes a retried upload idempotent server-side.
   */
  async uploadEvaluationPhoto(
    evaluationId: number,
    file: File | Blob,
    opts: { client_uuid: string; caption?: string | null; latitude?: number | null; longitude?: number | null; taken_at?: string | null; sort_order?: number; file_name?: string }
  ): Promise<ApiResponse<EvaluationMedia>> {
    const formData = new FormData();
    // A Blob replayed out of IndexedDB has no name; multer needs one to derive
    // the stored file extension.
    formData.append('media', file, opts.file_name || 'photo.jpg');
    formData.append('client_uuid', opts.client_uuid);
    if (opts.caption) formData.append('caption', opts.caption);
    if (opts.latitude != null) formData.append('latitude', String(opts.latitude));
    if (opts.longitude != null) formData.append('longitude', String(opts.longitude));
    if (opts.taken_at) formData.append('taken_at', opts.taken_at);
    if (opts.sort_order != null) formData.append('sort_order', String(opts.sort_order));

    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/evaluations/${evaluationId}/photos`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error?.error?.message || 'Upload failed') as Error & { status?: number };
      err.status = response.status;
      throw err;
    }
    return response.json();
  }

  // ===========================================================================
  // QUICK EVALUATIONS (cultivars not in the registry)
  // ===========================================================================

  async listQuickEvaluations(status: 'unmatched' | 'matched' | 'all' = 'unmatched'): Promise<ApiResponse<QuickEvaluationGroup[]>> {
    return apiService.get(`/cultivar/quick-evaluations?status=${status}`);
  }

  async createQuickEvaluation(data: QuickEvaluationInput): Promise<ApiResponse<QuickEvaluation>> {
    return apiService.post('/cultivar/quick-evaluations', data);
  }

  async updateQuickEvaluation(
    id: number,
    data: Partial<Pick<QuickEvaluation, 'name_seen' | 'crop_type_id' | 'overall_score' | 'location_label' | 'notes'>>
  ): Promise<ApiResponse<QuickEvaluation>> {
    return apiService.patch(`/cultivar/quick-evaluations/${id}`, data);
  }

  async deleteQuickEvaluation(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/quick-evaluations/${id}`);
  }

  async suggestQuickMatches(name: string, cropTypeId?: number | null): Promise<ApiResponse<QuickMatchSuggestion[]>> {
    const qs = new URLSearchParams({ name });
    if (cropTypeId) qs.append('crop_type_id', String(cropTypeId));
    return apiService.get(`/cultivar/quick-evaluations/suggest?${qs.toString()}`);
  }

  async matchQuickEvaluations(ids: number[], cultivarId: number): Promise<ApiResponse<QuickMatchResult>> {
    return apiService.post('/cultivar/quick-evaluations/match', { ids, cultivar_id: cultivarId });
  }

  async uploadQuickEvaluationPhoto(
    quickId: number,
    file: File | Blob,
    opts: { client_uuid: string; caption?: string | null; latitude?: number | null; longitude?: number | null; taken_at?: string | null; sort_order?: number; file_name?: string }
  ): Promise<ApiResponse<QuickEvaluationPhoto>> {
    const formData = new FormData();
    formData.append('media', file, opts.file_name || 'photo.jpg');
    formData.append('client_uuid', opts.client_uuid);
    if (opts.caption) formData.append('caption', opts.caption);
    if (opts.latitude != null) formData.append('latitude', String(opts.latitude));
    if (opts.longitude != null) formData.append('longitude', String(opts.longitude));
    if (opts.taken_at) formData.append('taken_at', opts.taken_at);
    if (opts.sort_order != null) formData.append('sort_order', String(opts.sort_order));

    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/quick-evaluations/${quickId}/photos`,
      { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: formData }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error?.error?.message || 'Upload failed') as Error & { status?: number };
      err.status = response.status;
      throw err;
    }
    return response.json();
  }

  async fetchQuickPhotoBlobUrl(photoId: number): Promise<string> {
    const token = getToken();
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    const resp = await fetch(`${base}/cultivar/quick-evaluations/photos/${photoId}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!resp.ok) throw new Error(`Failed to load photo (${resp.status})`);
    return URL.createObjectURL(await resp.blob());
  }

  // ===========================================================================
  // EVALUATION FOLLOW-UPS
  // ===========================================================================

  async listFollowups(filters?: { cultivar_id?: number; scope?: 'mine'; status?: string; due_before?: string }): Promise<ApiResponse<EvaluationFollowup[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.cultivar_id) queryParams.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.scope) queryParams.append('scope', filters.scope);
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.due_before) queryParams.append('due_before', filters.due_before);
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/evaluation-followups${qs ? `?${qs}` : ''}`);
  }

  async createFollowup(evaluationId: number, data: { due_date: string; focus_attribute_ids?: number[]; note?: string | null; assigned_to_user_id?: number | null }): Promise<ApiResponse<EvaluationFollowup>> {
    return apiService.post(`/cultivar/evaluations/${evaluationId}/followups`, data);
  }

  async resolveFollowup(followupId: number, status: 'completed' | 'dismissed', completedEvaluationId?: number | null): Promise<ApiResponse<EvaluationFollowup>> {
    return apiService.patch(`/cultivar/evaluation-followups/${followupId}`, {
      status,
      completed_evaluation_id: completedEvaluationId ?? null,
    });
  }

  // ===========================================================================
  // GROWING REGIONS
  // ===========================================================================

  async listGrowingRegions(filters?: { include_inactive?: boolean; country?: string }): Promise<ApiResponse<GrowingRegion[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.include_inactive) queryParams.append('include_inactive', 'true');
    if (filters?.country) queryParams.append('country', filters.country);
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/growing-regions${qs ? `?${qs}` : ''}`);
  }

  async createGrowingRegion(data: Partial<GrowingRegion> & { name: string }): Promise<ApiResponse<GrowingRegion>> {
    return apiService.post('/cultivar/growing-regions', data);
  }

  async updateGrowingRegion(id: number, data: Partial<GrowingRegion>): Promise<ApiResponse<GrowingRegion>> {
    return apiService.put(`/cultivar/growing-regions/${id}`, data);
  }

  async deactivateGrowingRegion(id: number): Promise<ApiResponse<GrowingRegion>> {
    return apiService.delete(`/cultivar/growing-regions/${id}`);
  }

  /** Nearest region to a GPS fix. Returns null beyond a sensible radius. */
  async findNearestRegion(latitude: number, longitude: number, maxDistanceKm?: number): Promise<ApiResponse<{ region: GrowingRegion; distance_km: number } | null>> {
    const queryParams = new URLSearchParams({ latitude: String(latitude), longitude: String(longitude) });
    if (maxDistanceKm) queryParams.append('max_distance_km', String(maxDistanceKm));
    return apiService.get(`/cultivar/growing-regions/nearest?${queryParams.toString()}`);
  }

  // ===========================================================================
  // CULTIVAR REGION MATURITY
  // ===========================================================================

  async listCultivarMaturity(cultivarId: number): Promise<ApiResponse<CultivarRegionMaturity[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/maturity`);
  }

  async saveMaturityRecord(data: {
    cultivar_id: number; growing_region_id: number; season_year: number;
    first_pick_date: string; last_pick_date?: string | null;
    harvest_window_days?: number | null; notes?: string | null;
  }): Promise<ApiResponse<CultivarRegionMaturity>> {
    return apiService.post('/cultivar/maturity', data);
  }

  async deleteMaturityRecord(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/maturity/${id}`);
  }

  /** Roll maturity_date observations into records. Never overwrites manual entries. */
  async deriveMaturityFromObservations(cultivarId: number): Promise<ApiResponse<CultivarRegionMaturity[]> & { derived_count?: number }> {
    return apiService.post(`/cultivar/registry/${cultivarId}/maturity/derive`, {});
  }

  // ===========================================================================
  // MATURITY ANALYTICS  (all computed on read — nothing is persisted)
  // ===========================================================================

  async getRegionOffsets(minSamples?: number): Promise<ApiResponse<RegionOffset[]>> {
    const qs = minSamples ? `?min_samples=${minSamples}` : '';
    return apiService.get(`/cultivar/maturity/region-offsets${qs}`);
  }

  async getSeasonIndex(growingRegionId: number, seasonYear: number): Promise<ApiResponse<SeasonIndex | null>> {
    return apiService.get(`/cultivar/maturity/season-index?growing_region_id=${growingRegionId}&season_year=${seasonYear}`);
  }

  /**
   * The maturity timeline across regions and seasons.
   *
   * Every filter is optional. Omitting the season lets the server pick the most
   * recent one carrying data — seasons share one calendar axis, so drawing them
   * all at once reads as a single impossibly busy year.
   */
  async getMaturityChart(filters: MaturityChartFilters = {}): Promise<ApiResponse<MaturityChart>> {
    return apiService.get(`/cultivar/maturity/master-chart${maturityChartQuery(filters)}`);
  }

  // ===========================================================================
  // EVALUATOR GRANTS
  // ===========================================================================

  async listEvaluatorGrants(filters?: { cultivar_id?: number; program_id?: number }): Promise<ApiResponse<EvaluatorGrant[]>> {
    const queryParams = new URLSearchParams();
    if (filters?.cultivar_id) queryParams.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.program_id) queryParams.append('program_id', String(filters.program_id));
    const qs = queryParams.toString();
    return apiService.get(`/cultivar/evaluator-grants${qs ? `?${qs}` : ''}`);
  }

  async createEvaluatorGrant(data: { cultivar_id?: number | null; program_id?: number | null; evaluator_org_id: number; notes?: string | null }): Promise<ApiResponse<EvaluatorGrant>> {
    return apiService.post('/cultivar/evaluator-grants', data);
  }

  async revokeEvaluatorGrant(id: number): Promise<ApiResponse<EvaluatorGrant>> {
    return apiService.delete(`/cultivar/evaluator-grants/${id}`);
  }

  // ===========================================================================
  // VISIBILITY CONFIG (per-cultivar override)
  // ===========================================================================

  async updateCultivarVisibilityConfig(cultivarId: number, config: VisibilityConfig): Promise<ApiResponse<{ id: number; visibility_config: VisibilityConfig }>> {
    return apiService.put(`/cultivar/registry/${cultivarId}/visibility-config`, config);
  }

  // ===========================================================================
  // CULTIVAR MEDIA
  // ===========================================================================

  async listCultivarMedia(cultivarId: number): Promise<ApiResponse<CultivarMedia[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/media`);
  }

  async uploadCultivarMedia(cultivarId: number, file: File, opts?: { kind?: string; caption?: string; visibility?: string; is_primary?: boolean; sort_order?: number }): Promise<ApiResponse<CultivarMedia>> {
    const formData = new FormData();
    formData.append('media', file);
    if (opts?.kind) formData.append('kind', opts.kind);
    if (opts?.caption) formData.append('caption', opts.caption);
    if (opts?.visibility) formData.append('visibility', opts.visibility);
    if (opts?.is_primary) formData.append('is_primary', String(opts.is_primary));
    if (opts?.sort_order != null) formData.append('sort_order', String(opts.sort_order));
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/registry/${cultivarId}/media`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error?.error?.message || 'Upload failed');
    }
    return response.json();
  }

  async updateCultivarMedia(mediaId: number, data: Partial<Pick<CultivarMedia, 'kind' | 'caption' | 'sort_order' | 'is_primary' | 'visibility'>>): Promise<ApiResponse<CultivarMedia>> {
    return apiService.put(`/cultivar/media/${mediaId}`, data);
  }

  async deleteCultivarMedia(mediaId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/media/${mediaId}`);
  }

  getCultivarMediaFileUrl(mediaId: number): string {
    return `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'}/cultivar/media/${mediaId}/file`;
  }

  async fetchCultivarMediaBlobUrl(mediaId: number): Promise<string> {
    const token = getToken();
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    const resp = await fetch(`${base}/cultivar/media/${mediaId}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!resp.ok) throw new Error(`Failed to load media (${resp.status})`);
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  }

  // =========================================================================
  // PROGRAM PRICE VERSIONS
  // =========================================================================

  async listPriceVersions(programId: number) {
    return apiService.get(`/cultivar/programs/${programId}/price-versions`);
  }

  async getCurrentPriceVersion(programId: number) {
    return apiService.get(`/cultivar/programs/${programId}/price-versions/current`);
  }

  async publishPriceVersion(programId: number, payload: Record<string, unknown>) {
    return apiService.post(`/cultivar/programs/${programId}/price-versions`, payload);
  }

  // =========================================================================
  // PROGRAM CONTRACT TEMPLATES
  // =========================================================================

  async listContractTemplates(programId: number) {
    return apiService.get(`/cultivar/programs/${programId}/contract-templates`);
  }

  async upsertContractTemplate(programId: number, role: string, payload: { family_id: number; required?: boolean | null; notes?: string | null }) {
    return apiService.put(`/cultivar/programs/${programId}/contract-templates/${role}`, payload);
  }

  async removeContractTemplate(programId: number, role: string) {
    return apiService.delete(`/cultivar/programs/${programId}/contract-templates/${role}`);
  }

  // =========================================================================
  // CULTIVAR ORDERING — new flow (PR3)
  // =========================================================================

  async listOrderingOrders(filters?: { status?: string; role?: 'nursery' | 'orchard'; cultivar_id?: number }) {
    const q = new URLSearchParams();
    if (filters?.status) q.append('status', filters.status);
    if (filters?.role) q.append('role', filters.role);
    if (filters?.cultivar_id) q.append('cultivar_id', String(filters.cultivar_id));
    const qs = q.toString();
    return apiService.get(`/cultivar/ordering/orders${qs ? `?${qs}` : ''}`);
  }

  async getOrderingOrder(id: number) {
    return apiService.get(`/cultivar/ordering/orders/${id}`);
  }

  async placeOrderingOrder(payload: Record<string, unknown>) {
    return apiService.post('/cultivar/ordering/orders', payload);
  }

  async nurseryAcceptOrder(id: number) {
    return apiService.post(`/cultivar/ordering/orders/${id}/nursery-accept`, {});
  }

  async markOrderFulfilled(id: number) {
    return apiService.post(`/cultivar/ordering/orders/${id}/fulfilled`, {});
  }

  async cancelOrderingOrder(id: number, reason?: string) {
    return apiService.post(`/cultivar/ordering/orders/${id}/cancel`, { reason: reason || null });
  }

  async retryDispatchOrder(id: number) {
    return apiService.post(`/cultivar/ordering/orders/${id}/dispatch`, {});
  }

  async remindOrderDocument(orderId: number, docId: number, note?: string) {
    return apiService.post(`/cultivar/ordering/orders/${orderId}/documents/${docId}/remind`, { note: note || null });
  }

  // Authenticated blob download for the signed PDF of one order document.
  // Triggers a browser save dialog. Returns true on success.
  async downloadOrderDocumentSignedPdf(orderId: number, docId: number, filename = `order-${orderId}-document.pdf`): Promise<boolean> {
    const token = getToken();
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    const url = `${base}/cultivar/ordering/orders/${orderId}/documents/${docId}/signed-pdf`;
    const resp = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!resp.ok) return false;
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return true;
  }

  // =========================================================================
  // PROGRAM APPROVERS (PR4)
  // =========================================================================

  async listProgramApprovers(programId: number) {
    return apiService.get(`/cultivar/programs/${programId}/approvers`);
  }

  async addProgramApprover(programId: number, payload: Record<string, unknown>) {
    return apiService.post(`/cultivar/programs/${programId}/approvers`, payload);
  }

  async removeProgramApprover(programId: number, approverOrgId: number, role: string) {
    return apiService.delete(`/cultivar/programs/${programId}/approvers/${approverOrgId}/${role}`);
  }

  // =========================================================================
  // ORDER APPROVALS (approver-side queue)
  // =========================================================================

  async listPendingApprovals() {
    return apiService.get(`/cultivar/ordering/approvals`);
  }

  async approveOrder(approvalId: number, notes?: string) {
    return apiService.post(`/cultivar/ordering/approvals/${approvalId}/approve`, { notes: notes || null });
  }

  async rejectOrder(approvalId: number, notes?: string) {
    return apiService.post(`/cultivar/ordering/approvals/${approvalId}/reject`, { notes: notes || null });
  }

  async listLicenses(filters?: { role?: 'licensor' | 'licensee'; status?: string; license_tier?: string; cultivar_id?: number; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (filters?.role) q.append('role', filters.role);
    if (filters?.status) q.append('status', filters.status);
    if (filters?.license_tier) q.append('license_tier', filters.license_tier);
    if (filters?.cultivar_id) q.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.page) q.append('page', String(filters.page));
    if (filters?.limit) q.append('limit', String(filters.limit));
    const qs = q.toString();
    return apiService.get(`/cultivar/licenses${qs ? `?${qs}` : ''}`);
  }

  // =========================================================================
  // CULTIVAR ORDER API KEYS (PR5)
  // =========================================================================

  async listOrderApiKeys() {
    return apiService.get(`/cultivar/ordering/api-keys`);
  }

  async createOrderApiKey(payload: { regional_license_id: number; label?: string | null }) {
    return apiService.post(`/cultivar/ordering/api-keys`, payload);
  }

  async revokeOrderApiKey(id: number) {
    return apiService.post(`/cultivar/ordering/api-keys/${id}/revoke`, {});
  }
}

export const cultivarService = new CultivarService();
export default cultivarService;
